import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  createColumns,
  GROUPING_RANGE_DATES,
  type GroupingRange,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import BadgeList from "@/v2-backoffice-client/components/badgeList"
import { getLicenseProductPillProps } from "@/v2-backoffice-client/license/product/license.product.utils"
import LicenseAddOnCatalogRowToolbar from "@/v2-backoffice-client/license/addOns/catalog/LicenseAddOnCatalogRowToolbar"
import { useAddOnCatalogQuery } from "@/v2-backoffice-client/license/addOns/catalog/license.addOn.catalog.query"
import type { AddOnCatalogFragment } from "@/v2-backoffice-client/license/addOns/catalog/__types__/license.addOn.catalog.fragment"

const CATALOG_SCOPE = "Catalog"
const BESPOKE_SCOPE = "Bespoke"

// Scope is derived per row so the pills can filter on it and grouping/export
// read a real column.
type AddOnCatalogRow = AddOnCatalogFragment & {
  Scope: typeof CATALOG_SCOPE | typeof BESPOKE_SCOPE
}

// Customer-footprint bands — "Nobody" is the retire-candidate bucket.
const GROUPING_RANGE_GRANTS: GroupingRange[] = [
  { label: "Nobody", test: (v) => !v || Number(v) <= 0 },
  { label: "1 – 5 customers", test: (v) => Number(v) <= 5 },
  { label: "6+ customers", test: () => true },
]

function createAddOnCatalogColumns() {
  return createColumns<AddOnCatalogRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      Name: {
        id: "Name",
        label: "Add-on",
        defaultSize: 200,
        to: ({ row }) => `?v=addOn:${row.original.Id}`,
      },
      Code: {
        id: "Code",
        label: "Code",
        defaultSize: 120,
        filter: { type: "text", placeholder: "Code..." },
        getValue: (row) => row.Code ?? "",
        renderCell({ row }) {
          const value = row.original.Code
          if (!value) return null
          return (
            <Badge size="sm" variant="neutral">
              {value}
            </Badge>
          )
        },
        exportValue({ row }) {
          return row.original.Code ?? ""
        },
      },
      Scope: {
        id: "Scope",
        label: "Scope",
        description:
          "Catalog add-ons may be granted to any customer; a bespoke add-on only ever to the one customer it was composed for.",
        defaultSize: 160,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any scope",
          options: [
            { label: CATALOG_SCOPE, value: CATALOG_SCOPE },
            {
              label: BESPOKE_SCOPE,
              value: BESPOKE_SCOPE,
              dotVariant: "warning",
            },
          ],
        },
        renderCell({ row }) {
          if (row.original.Scope === CATALOG_SCOPE) {
            return <Badge size="sm">Catalog</Badge>
          }
          return (
            <Badge size="sm" variant="neutral">
              {row.original.CustomerName ?? "Bespoke"}
            </Badge>
          )
        },
        exportValue({ row }) {
          return row.original.Scope === CATALOG_SCOPE
            ? CATALOG_SCOPE
            : `${BESPOKE_SCOPE}: ${row.original.CustomerName ?? ""}`
        },
      },
      Products: {
        id: "Products",
        label: "Goes with",
        description:
          "The license a customer must hold for this add-on to work: the Sweden add-on goes with a Placepoint MCP (Norway) license. Adding it to a customer without that license is allowed and does nothing until they get one. Empty = any license.",
        defaultSize: 200,
        filter: { type: "text", placeholder: "Product..." },
        getValue: (row) =>
          row.Products.map((product) => product.Name).join(", "),
        renderCell({ row }) {
          return (
            <BadgeList
              items={row.original.Products.map((product) => ({
                key: String(product.Code),
                label: product.Name,
                ...getLicenseProductPillProps(product.Name),
                hint: "license",
                tooltip: `A ${product.Name} license`,
              }))}
              overflowTitle="Goes with"
              emptyLabel={
                <span className="text-muted-foreground">Any license</span>
              }
            />
          )
        },
        exportValue({ row }) {
          return (
            row.original.Products.map((product) => product.Name).join(", ") ||
            "Any license"
          )
        },
      },
      FeatureGroupNames: {
        id: "FeatureGroupNames",
        label: "Unlocks",
        description:
          "The packages a customer gets on top of their license when they have this add-on. Empty = can be added but gives nothing yet. The text filter matches any single package name.",
        defaultSize: 260,
        filter: { type: "text", placeholder: "Package name..." },
        getValue: (row) =>
          row.FeatureGroups.map((featureGroup) => featureGroup.Name).join(", "),
        renderCell({ row }) {
          return (
            <BadgeList
              items={row.original.FeatureGroups.map((featureGroup) => {
                const features = `${featureGroup.FeatureCount} ${featureGroup.FeatureCount === 1 ? "feature" : "features"}`
                return {
                  key: String(featureGroup.Id),
                  label: featureGroup.Name,
                  to: `?v=featureGroup:${featureGroup.Id}`,
                  hint: features,
                  tooltip: `${features}. Open the package.`,
                }
              })}
              overflowTitle="Unlocks"
              emptyLabel={<span className="text-destructive">Nothing yet</span>}
            />
          )
        },
        exportValue({ row }) {
          return row.original.FeatureGroups.map(
            (featureGroup) => featureGroup.Name,
          ).join(", ")
        },
      },
      GrantCount: {
        id: "GrantCount",
        label: "Customers",
        description:
          "Customers who have this add-on. Group on it to split the sold add-ons from the long tail nobody has.",
        defaultSize: 70,
        filter: { type: "number-range" },
        groupingRanges: GROUPING_RANGE_GRANTS,
        renderCell({ row }) {
          return row.original.GrantCount || null
        },
        exportValue({ row }) {
          return row.original.GrantCount
        },
        // A zero the cell leaves blank is a blank, not a value to average.
        summaryValue({ row }) {
          return row.original.GrantCount || null
        },
      },
      Description: {
        id: "Description",
        label: "Description",
        defaultSize: 240,
        defaultHidden: true,
        renderCell({ row }) {
          return (
            <span className="truncate text-muted-foreground">
              {row.original.Description}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.Description ?? ""
        },
      },
      UpdatedAt: {
        id: "UpdatedAt",
        label: "Updated",
        defaultSize: 100,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.UpdatedAt
          return value ? formatDateString(String(value)) : null
        },
      },
      CreatedAt: {
        id: "CreatedAt",
        label: "Created",
        defaultSize: 100,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.CreatedAt
          return value ? formatDateString(String(value)) : null
        },
      },
      Id: { id: "Id", label: "Id", defaultSize: 50, defaultHidden: true },
    },
  })
}

export type LicenseAddOnCatalogTableProps = Omit<
  TableProps<AddOnCatalogRow>,
  "data" | "columns"
>

// The add-on management board: every live add-on — shared catalog and
// bespoke — with scope, contents and grant footprint. Composing add-ons
// happens here (creation in the page header, LicenseAddOnCreateButton);
// granting them to licenses stays on the customer panel. Owns the catalog
// query (shared via the Apollo cache with the tab trigger).
function LicenseAddOnCatalogTable(props: LicenseAddOnCatalogTableProps) {
  const { className, ...tableProps } = props

  const { data, previousData, loading, error, refetch } = useAddOnCatalogQuery()
  // previousData keeps rows rendered through post-save evictions.
  const addOns = (data ?? previousData)?.getAddOnCatalog ?? []

  const rows = useMemo<AddOnCatalogRow[]>(
    () =>
      addOns.map((addOn) => ({
        ...addOn,
        Scope: addOn.CustomerId == null ? CATALOG_SCOPE : BESPOKE_SCOPE,
      })),
    [addOns],
  )

  const bespokeCount = useMemo(
    () => rows.filter((row) => row.Scope === BESPOKE_SCOPE).length,
    [rows],
  )

  const columns = useMemo(() => createAddOnCatalogColumns(), [])

  return (
    <Table
      className={cn("license-add-on-catalog-table", className)}
      tableId="license-add-on-catalog-table"
      data={rows}
      columns={columns}
      enableGrouping
      {...tableProps}
    >
      <Table.Toolbar>
        {/* Namespaced: the boards share the Licensing page, so the default
            "status" param would leak pill state between tabs. */}
        <Table.StatusFilter
          column="Scope"
          urlParam="addons-scope"
          isLoading={loading}
        >
          <Table.StatusFilter.Option value={null} count={rows.length}>
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={0}
            filterValue={CATALOG_SCOPE}
            iconSrc="apps"
            count={rows.length - bespokeCount}
          >
            Catalog
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={1}
            filterValue={BESPOKE_SCOPE}
            iconSrc="user"
            count={bespokeCount}
          >
            Bespoke
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search column="Name" placeholder="Search by add-on name..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => void refetch()}
          exportFileName="add-ons"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<AddOnCatalogRow> column="Name">
        {(addOn) => <LicenseAddOnCatalogRowToolbar addOn={addOn} />}
      </Table.RowToolbar>

      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default LicenseAddOnCatalogTable
